import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CacheConfigService } from '../services/cache-config.service';

/**
 * HTTP Cache Interceptor - Phase 3A Implementation
 *
 * Provides intelligent caching for static and semi-static API endpoints
 * to reduce duplicate API calls and improve application performance.
 *
 * Features:
 * - Selective caching based on endpoint configuration
 * - TTL-based cache expiration
 * - Automatic cleanup of expired entries
 * - Cache invalidation support
 * - Memory-efficient storage
 */
@Injectable()
export class HttpCacheInterceptor implements HttpInterceptor {
  private cache = new Map<string, {
    response: HttpResponse<any>;
    timestamp: number;
    ttl: number;
    endpoint: string;
  }>();

  private readonly MAX_CACHE_SIZE = 500; // Maximum number of cached entries
  private readonly CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes

  constructor(private cacheConfig: CacheConfigService) {
    // Setup periodic cleanup
    setInterval(() => this.cleanup(), this.CLEANUP_INTERVAL);

    // Register interceptor on window for cache invalidation service access
    (window as any)._cacheInterceptor = this;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Explicit per-request cache bypass (e.g. refetch right after a write).
    // Strip the marker header and skip both cache read and write so the
    // caller always gets fresh data from the network.
    if (req.headers.has('X-Skip-Cache')) {
      return next.handle(req.clone({ headers: req.headers.delete('X-Skip-Cache') }));
    }

    // Only cache GET requests
    if (req.method !== 'GET') {
      return next.handle(req);
    }

    // Check if this endpoint should be cached
    const config = this.cacheConfig.getConfigForUrl(req.url);
    if (!config) {
      return next.handle(req);
    }

    // Generate cache key
    const cacheKey = this.generateCacheKey(req);
    const cached = this.cache.get(cacheKey);

    // Check if we have valid cached data
    if (cached && this.isValidCache(cached)) {
      console.log(`🎯 Cache HIT: ${req.url.substring(0, 50)}...`);

      // Clone the response to avoid issues with multiple subscribers
      const clonedResponse = cached.response.clone();
      return of(clonedResponse);
    }

    // Cache miss - make request and cache response
    console.log(`🌐 Cache MISS: ${req.url.substring(0, 50)}...`);

    return next.handle(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse && event.status === 200) {
          // Only cache successful responses
          this.setCacheEntry(cacheKey, event, config.ttl, req.url);

          // Enforce cache size limit
          this.enforceCacheLimit();
        }
      })
    );
  }

  /**
   * Generate a unique cache key for the request
   */
  private generateCacheKey(req: HttpRequest<any>): string {
    const params = req.params.toString();
    const headers = this.getRelevantHeaders(req);
    return `${req.method}-${req.url}${params ? '-' + params : ''}${headers ? '-' + headers : ''}`;
  }

  /**
   * Get relevant headers that affect cache key (e.g., Authorization)
   */
  private getRelevantHeaders(req: HttpRequest<any>): string {
    const relevantHeaders = ['authorization'];
    const headerValues: string[] = [];

    relevantHeaders.forEach(header => {
      const value = req.headers.get(header);
      if (value) {
        // Only use part of auth token to avoid huge cache keys
        headerValues.push(header + ':' + value.substring(0, 20));
      }
    });

    return headerValues.join(',');
  }

  /**
   * Check if cached data is still valid
   */
  private isValidCache(cached: any): boolean {
    return Date.now() - cached.timestamp < cached.ttl;
  }

  /**
   * Set cache entry with metadata
   */
  private setCacheEntry(key: string, response: HttpResponse<any>, ttl: number, url: string): void {
    this.cache.set(key, {
      response: response.clone(),
      timestamp: Date.now(),
      ttl: ttl,
      endpoint: url
    });
  }

  /**
   * Enforce maximum cache size by removing oldest entries
   */
  private enforceCacheLimit(): void {
    if (this.cache.size <= this.MAX_CACHE_SIZE) {
      return;
    }

    // Convert to array and sort by timestamp (oldest first)
    const entries = Array.from(this.cache.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp);

    // Remove oldest entries until we're under the limit
    const entriesToRemove = this.cache.size - this.MAX_CACHE_SIZE;
    for (let i = 0; i < entriesToRemove; i++) {
      this.cache.delete(entries[i][0]);
    }

    console.log(`🧹 Cache limit enforced. Removed ${entriesToRemove} old entries.`);
  }

  /**
   * Remove expired cache entries
   */
  private cleanup(): void {
    const now = Date.now();
    let removedCount = 0;

    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > value.ttl) {
        this.cache.delete(key);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      console.log(`🧹 Cache cleanup: Removed ${removedCount} expired entries`);
    }
  }

  /**
   * Public method for cache invalidation
   * @param pattern - URL pattern to match for cache clearing
   */
  clearCache(pattern?: string): void {
    if (!pattern) {
      const size = this.cache.size;
      this.cache.clear();
      console.log(`🧹 Cleared entire cache (${size} entries)`);
      return;
    }

    let removedCount = 0;
    for (const [key, value] of this.cache.entries()) {
      if (value.endpoint.includes(pattern)) {
        this.cache.delete(key);
        removedCount++;
      }
    }

    console.log(`🧹 Cleared cache for pattern "${pattern}" (${removedCount} entries)`);
  }

  /**
   * Get cache statistics for monitoring
   */
  getStats(): any {
    const now = Date.now();
    const entries = Array.from(this.cache.values());

    const validEntries = entries.filter(entry =>
      now - entry.timestamp < entry.ttl
    );

    const endpointStats = entries.reduce((acc, entry) => {
      const endpoint = entry.endpoint.split('?')[0]; // Remove query params
      acc[endpoint] = (acc[endpoint] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalEntries: this.cache.size,
      validEntries: validEntries.length,
      expiredEntries: entries.length - validEntries.length,
      oldestEntry: entries.length > 0 ? Math.min(...entries.map(e => e.timestamp)) : null,
      newestEntry: entries.length > 0 ? Math.max(...entries.map(e => e.timestamp)) : null,
      endpointBreakdown: endpointStats,
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  /**
   * Estimate memory usage of cache
   */
  private estimateMemoryUsage(): string {
    let totalSize = 0;

    for (const [key, value] of this.cache.entries()) {
      // Rough estimation
      totalSize += key.length * 2; // UTF-16 encoding
      totalSize += JSON.stringify(value.response.body).length * 2;
      totalSize += 100; // Overhead for metadata
    }

    // Convert to human readable format
    if (totalSize < 1024) return totalSize + ' B';
    if (totalSize < 1024 * 1024) return Math.round(totalSize / 1024) + ' KB';
    return Math.round(totalSize / (1024 * 1024)) + ' MB';
  }

  /**
   * Warm cache by pre-loading common endpoints
   * Call this method after user login to pre-populate cache
   */
  warmCache(urls: string[]): void {
    // Implementation would depend on your API service
    console.log(`🔥 Cache warming initiated for ${urls.length} URLs`);
    // This would typically make HTTP calls to warm the cache
  }
}